import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export function PartyConfetti() {
  const anchor = useRef<HTMLSpanElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const card = anchor.current?.closest('.modal-card');
    const surface = canvas.current;
    if (!card || !surface || motion.matches) return;
    const context = surface.getContext('2d');
    if (!context) return;
    const random = (min: number, max: number) => min + Math.random() * (max - min);
    const windPhase = random(0, Math.PI * 2);
    const windStrength = random(12, 28);
    const particles = Array.from({ length: 6 }, (_, burst) => {
      const top = burst < 3;
      const origin = .15 + (burst % 3) * .35 + random(-.045, .045);
      const power = random(240, 340);
      const launch = 0;
      const lean = random(-.2, .2);
      return Array.from({ length: Math.floor(random(18, 29)) }, () => {
        const spread = random(-.9, .9) + lean;
        const speed = power * random(.75, 1.2);
        return {
          top, origin: origin + random(-.02, .02), delay: launch,
          x: 0, y: 0, vx: Math.sin(spread) * speed,
          vy: (top ? -1 : .65) * Math.cos(spread) * speed,
          angle: random(0, Math.PI * 2), spin: random(-5, 5), landed: false,
          drag: random(1.3, 2.8), phase: random(0, Math.PI * 2),
          flutter: random(3, 7), width: random(3, 5.5), height: random(3, 7),
          life: random(3.7, 4.7),
          color: ['#760135', '#FDBE27', '#9B2450', '#FFD36A'][Math.floor(random(0, 4))],
        };
      });
    }).flat();
    let frame = 0;
    let previous = 0;
    let elapsed = 0;
    const stop = () => { cancelAnimationFrame(frame); context.clearRect(0, 0, surface.width, surface.height); };
    const draw = (time: number) => {
      const dt = previous ? Math.min((time - previous) / 1000, .032) : 0;
      previous = time; elapsed += dt;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      if (surface.width !== Math.round(innerWidth * ratio) || surface.height !== Math.round(innerHeight * ratio)) {
        surface.width = Math.round(innerWidth * ratio); surface.height = Math.round(innerHeight * ratio);
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, innerWidth, innerHeight);
      const rect = card.getBoundingClientRect();
      particles.forEach((p) => {
        const age = elapsed - p.delay;
        if (age < 0 || age > p.life) return;
        if (!p.landed) {
          // Gravity with air drag gives light paper a slow terminal fall speed.
          const wind = Math.sin(elapsed * 1.6 + windPhase) * windStrength;
          const flutter = Math.sin(age * p.flutter + p.phase);
          const previousY = p.y;
          p.vy += (240 - p.drag * p.vy + flutter * 18) * dt;
          p.vx += (wind + flutter * 14 - p.vx) * p.drag * .7 * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.angle += (p.spin + flutter) * dt;
          const landingX = rect.left + p.origin * rect.width + p.x;
          if (p.top && previousY < 0 && p.y >= 0 && p.vy > 0 && landingX >= rect.left + 12 && landingX <= rect.right - 12) {
            p.y = 0; p.landed = true;
          }
        }
        const x = rect.left + p.origin * rect.width + p.x;
        const y = (p.top ? rect.top - 3 : rect.bottom + 3) + p.y;
        context.save(); context.translate(x, y); context.rotate(p.angle);
        context.globalAlpha = .9 * Math.max(0, Math.min(1, p.life - age));
        context.fillStyle = p.color;
        const height = p.landed ? 2 : 1 + Math.abs(Math.sin(age * p.flutter + p.phase)) * p.height;
        context.fillRect(-p.width / 2, -height / 2, p.width, height);
        context.restore();
      });
      if (elapsed < 6.5) frame = requestAnimationFrame(draw);
      else stop();
    };
    frame = requestAnimationFrame(draw);
    motion.addEventListener('change', stop);
    return () => { stop(); motion.removeEventListener('change', stop); };
  }, []);
  return <><span ref={anchor} hidden aria-hidden="true" />{createPortal(<canvas ref={canvas} aria-hidden="true" className="party-confetti-canvas" />, document.body)}</>;
}
